/**
 * My Bookings Page
 * Display user's booking history
 */

import { t, formatDate, formatCurrency } from '../i18n';
import { Header, Card } from '../components';
import { apiFetch } from '../api';
import { getProfile, closeWindow } from '../liff';
import type { Booking } from '../types';

/**
 * Show success toast notification (instead of alert to avoid domain name)
 */
function showSuccessToast(message: string): void {
  showToast(message, 'success');
}

/**
 * Show error toast notification
 */
function showErrorToast(message: string): void {
  showToast(message, 'error');
}

/**
 * Show toast notification
 */
function showToast(message: string, type: 'success' | 'error' = 'success'): void {
  // Remove existing toast
  const existingToast = document.getElementById('custom-toast');
  if (existingToast) existingToast.remove();

  const toast = document.createElement('div');
  toast.id = 'custom-toast';
  toast.style.cssText = `
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: ${type === 'success' ? '#00B900' : '#dc3545'};
    color: white;
    padding: 20px 32px;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    box-shadow: 0 8px 32px rgba(0,0,0,0.3);
    z-index: 99999;
    text-align: center;
    max-width: 80%;
    animation: toastFadeIn 0.3s ease;
  `;
  toast.innerHTML = `
    <div style="font-size: 32px; margin-bottom: 8px;">${type === 'success' ? '✅' : '❌'}</div>
    <div>${message}</div>
  `;

  // Add animation style
  const style = document.createElement('style');
  style.textContent = `
    @keyframes toastFadeIn {
      from { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
      to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
    }
    @keyframes toastFadeOut {
      from { opacity: 1; transform: translate(-50%, -50%) scale(1); }
      to { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
    }
  `;
  document.head.appendChild(style);
  document.body.appendChild(toast);

  // Auto remove after 2 seconds
  setTimeout(() => {
    toast.style.animation = 'toastFadeOut 0.3s ease forwards';
    setTimeout(() => toast.remove(), 300);
  }, 2000);
}

/**
 * Render my bookings page
 */
export function renderMyBookingsPage(): string {
  return `
    ${Header(t('myBookings'), false)}
    
    <div class="page-content">
      <div id="bookings-list">
        <div style="text-align: center; padding: 60px 20px;">
          <div class="spinner"></div>
          <p style="margin-top: 20px; color: #666;">${t('loading')}</p>
        </div>
      </div>
    </div>

    <!-- Floating scroller (Top / Bottom) -->
    <div id="bookings-scroller" style="position:fixed; right:16px; bottom:24px; display:flex; flex-direction:column; gap:8px; z-index:2000;">
      <button id="scroll-to-top" aria-label="Scroll to top" style="display:none; align-items:center; justify-content:center; width:44px; height:44px; border-radius:50%; background:#00B900; color:#fff; border:none; box-shadow:0 4px 12px rgba(0,0,0,0.2); font-size:18px;">▲</button>
      <button id="scroll-to-bottom" aria-label="Scroll to bottom" style="display:flex; align-items:center; justify-content:center; width:44px; height:44px; border-radius:50%; background:#00B900; color:#fff; border:none; box-shadow:0 4px 12px rgba(0,0,0,0.2); font-size:18px;">▼</button>
    </div>
  `;
}

/**
 * Initialize my bookings page
 */
export async function initMyBookingsPage(): Promise<void> {
  console.log('[MyBookings] Initializing...');

  const container = document.getElementById('bookings-list');
  if (!container) {
    console.error('[MyBookings] Container not found');
    return;
  }

  try {
    // Get user profile from LIFF
    const profile = await getProfile();
    if (!profile) {
      console.error('[MyBookings] No profile found');
      container.innerHTML = `
        <div style="text-align: center; padding: 60px 20px;">
          <div style="font-size: 80px; margin-bottom: 20px;">❌</div>
          <h3 style="color: #666; margin-bottom: 10px;">${t('error')}</h3>
          <p style="color: #999; font-size: 14px;">Unable to get user profile</p>
        </div>
      `;
      return;
    }

    console.log('[MyBookings] User ID:', profile.userId);

    // Get tenant ID
    const { getTenantId } = await import('../config');
    const tenantId = getTenantId();
    console.log('[MyBookings] Tenant ID:', tenantId);

    // Fetch user's bookings
    const result = await apiFetch<{
      success: boolean;
      data: { lineUserId: string; totalBookings: number; bookings: Booking[]; cancellationDeadlineDays?: number };
    }>(`/api/${tenantId}/bookings/user/${profile.userId}`);

    console.log('[MyBookings] API result:', result);

    if (
      !result.success ||
      !result.data ||
      !result.data.bookings ||
      result.data.bookings.length === 0
    ) {
      console.log('[MyBookings] No bookings found');
      renderNoBookings();
      return;
    }

    console.log('[MyBookings] Rendering bookings:', result.data.bookings.length);
    const deadlineDays = result.data.cancellationDeadlineDays ?? 3;
    renderBookingsList(result.data.bookings, deadlineDays);

    // Initialize floating scroller buttons (Top / Bottom)
    try {
      const scrollerTop = document.getElementById('scroll-to-top') as HTMLButtonElement | null;
      const scrollerBottom = document.getElementById(
        'scroll-to-bottom'
      ) as HTMLButtonElement | null;
      const listEl = document.getElementById('bookings-list');
      if (!listEl) return;

      function updateScrollerVisibility() {
        if (!scrollerTop || !scrollerBottom || !listEl) return;
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        scrollerTop.style.display = scrollTop > 150 ? 'flex' : 'none';
        const nearBottom =
          window.innerHeight + scrollTop >= document.documentElement.scrollHeight - 150;
        scrollerBottom.style.display = nearBottom ? 'none' : 'flex';
      }

      scrollerTop?.addEventListener('click', () => {
        // Scroll to absolute top of the page
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });

      scrollerBottom?.addEventListener('click', () => {
        listEl.scrollIntoView({ behavior: 'smooth', block: 'end' });
        window.scrollTo({
          top: document.documentElement.scrollHeight - window.innerHeight,
          behavior: 'smooth',
        });
      });

      window.addEventListener('scroll', updateScrollerVisibility);
      window.addEventListener('resize', updateScrollerVisibility);
      // initial
      updateScrollerVisibility();
    } catch (e) {
      console.error('[MyBookings] Failed to initialize scroller:', e);
    }
  } catch (error) {
    console.error('[MyBookings] Error:', error);

    // Render error state
    if (container) {
      container.innerHTML = `
        <div style="text-align: center; padding: 60px 20px;">
          <div style="font-size: 80px; margin-bottom: 20px;">❌</div>
          <h3 style="color: #666; margin-bottom: 10px;">${t('error')}</h3>
          <p style="color: #999; font-size: 14px;">${t('errorNetwork')}</p>
        </div>
      `;
    }
  }
}

/**
 * Render no bookings message
 */
function renderNoBookings(): void {
  const container = document.getElementById('bookings-list');
  if (!container) return;

  container.innerHTML = `
    <div style="text-align: center; padding: 60px 20px;">
      <div style="font-size: 80px; margin-bottom: 20px;">📋</div>
      <h3 style="color: #666; margin-bottom: 10px;">${t('noBookings')}</h3>
      <p style="color: #999; font-size: 14px;">${t('noBookingsDesc')}</p>
      <button 
        type="button" 
        class="btn btn-primary" 
        style="margin-top: 30px;"
        onclick="window.navigateToSearch()"
      >
        ${t('startBooking')}
      </button>
      <button 
        type="button" 
        class="btn btn-orange" 
        style="margin-top: 15px;"
        onclick="window.closeMyBookings()"
      >
        ${t('close')}
      </button>
    </div>
  `;

  window.navigateToSearch = () => {
    window.location.href = 'https://liff.line.me/2008769287-RUpr06Uk?page=booking';
  };

  window.closeMyBookings = () => {
    closeWindow();
  };
}

/**
 * Check if booking can be modified/cancelled
 * @param deadlineDays - min days before check-in required (from tenant settings, default 3)
 */
function canModifyBooking(checkInDate: string, deadlineDays: number = 3): boolean {
  const checkIn = new Date(checkInDate);
  const today = new Date();
  const diffTime = checkIn.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays >= deadlineDays;
}

/**
 * Render bookings list
 */
function renderBookingsList(bookings: Booking[], deadlineDays: number = 3): void {
  const container = document.getElementById('bookings-list');
  if (!container) return;

  const bookingsHtml = bookings
    .map(booking => {
      const statusColor =
        booking.status === 'confirmed'
          ? '#00B900'
          : booking.status === 'cancelled'
            ? '#999999'
            : '#FF9500';
      const statusMap: Record<string, string> = {
        confirmed: t('statusConfirmed'),
        cancelled: t('statusCancelled'),
        completed: t('statusCompleted'),
        pending: t('statusPending'),
      };
      const statusText = statusMap[booking.status] || booking.status;

      const statusIcon =
        booking.status === 'confirmed'
          ? '✓'
          : booking.status === 'cancelled'
            ? '✗'
            : booking.status === 'completed'
              ? '✓'
              : '⏳';

      return Card(
        `
      <div class="booking-card">
        <div class="booking-header">
          <div class="booking-status" style="color: ${statusColor};">
            <span class="status-icon">${statusIcon}</span>
            <span class="status-text">${statusText}</span>
          </div>
          <div class="booking-date">${formatDate(booking.createdAt)}</div>
        </div>
        
        <div class="booking-confirmation">
          <div class="label">${t('confirmationNumber')}</div>
          <div class="value" style="color: ${statusColor}; font-weight: bold; font-size: 18px;">
            ${booking.confirmationNumber}
          </div>
        </div>
        
        <div class="booking-details">
          ${
            booking.bookingType === 'SPA' || (booking as any).spaDetails
              ? `
          <div class="detail-row">
            <span class="detail-label">🌿 ประเภทบริการ</span>
            <span class="detail-value" style="font-weight:bold; color:#7e22ce;">สปา (Spa Treatment)</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">💆 ทรีตเมนต์</span>
            <span class="detail-value">${(booking as any).spaDetails?.treatmentName?.th || booking.roomTypeName || 'บริการสปา'}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">⏰ วันเวลาจอง</span>
            <span class="detail-value">${formatDate((booking as any).spaDetails?.bookingDate || booking.checkIn)} @ ${(booking as any).spaDetails?.timeSlot || '-'} น.</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">💳 การชำระเงิน</span>
            <span class="detail-value" style="color:#0284c7; font-weight:600;">ชำระเงินที่ร้าน (Pay at Venue)</span>
          </div>
              `
              : booking.bookingType === 'RESTAURANT' || (booking as any).restaurantDetails
                ? `
          <div class="detail-row">
            <span class="detail-label">🍽️ ประเภทบริการ</span>
            <span class="detail-value" style="font-weight:bold; color:#ea580c;">จองโต๊ะอาหาร (Restaurant)</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">👥 จำนวนที่นั่ง</span>
            <span class="detail-value">${(booking as any).restaurantDetails?.partySize || booking.adults || 2} ท่าน (${(booking as any).restaurantDetails?.seatingArea || 'Indoor'})</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">⏰ วันเวลาจอง</span>
            <span class="detail-value">${formatDate((booking as any).restaurantDetails?.bookingDate || booking.checkIn)} @ ${(booking as any).restaurantDetails?.timeSlot || '-'} น.</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">💳 การชำระเงิน</span>
            <span class="detail-value" style="color:#0284c7; font-weight:600;">ชำระเงินที่ร้าน (Pay at Venue)</span>
          </div>
              `
                : `
          <div class="detail-row">
            <span class="detail-label">🏨 ${t('roomType')}</span>
            <span class="detail-value">${booking.roomTypeName || ''}</span>
          </div>
          ${
            booking.ratePlanName
              ? `
          <div class="detail-row">
            <span class="detail-label">📋 ${t('ratePlan')}</span>
            <span class="detail-value">${booking.ratePlanName}${booking.isRefundable === false ? ` <span style="display:inline-block; background:#dc2626; color:#fff; font-size:11px; font-weight:600; padding:1px 7px; border-radius:4px; margin-left:4px;">${t('nonRefundable')}</span>` : ''}</span>
          </div>
          `
              : ''
          }
          <div class="detail-row">
            <span class="detail-label">📅 ${t('checkIn')}</span>
            <span class="detail-value">${formatDate(booking.checkIn)}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">📅 ${t('checkOut')}</span>
            <span class="detail-value">${formatDate(booking.checkOut)}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">🌙 ${t('nights')}</span>
            <span class="detail-value">${booking.nights || '-'}</span>
          </div>
          `
          }
          <div class="detail-row">
            <span class="detail-label">🚪 ${t('rooms')}</span>
            <span class="detail-value">${booking.rooms}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">👥 ${t('guests')}</span>
            <span class="detail-value">${booking.adults} ${t('adults')}${booking.children > 0 ? `, ${booking.children} ${t('children')}` : ''}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">👤 ${t('guestName')}</span>
            <span class="detail-value">${booking.guestFirstName && booking.guestLastName ? `${booking.guestFirstName} ${booking.guestLastName}` : '-'}</span>
          </div>
          ${
            booking.guestEmail
              ? `
          <div class="detail-row">
            <span class="detail-label">📧 ${t('email')}</span>
            <span class="detail-value" style="font-size: 12px;">${booking.guestEmail}</span>
          </div>
          `
              : ''
          }
          ${
            booking.specialRequests
              ? `
          <div class="detail-row" style="flex-direction: column; align-items: flex-start;">
            <span class="detail-label">📝 ${t('specialRequests')}</span>
            <span class="detail-value" style="margin-top: 4px; font-size: 13px; color: #666; font-style: italic;">${booking.specialRequests}</span>
          </div>
          `
              : ''
          }
          
          <!-- Price Breakdown -->
          <div style="margin-top: 12px; padding-top: 12px; border-top: 1px dashed #e5e5e5;">
            ${
              booking.ratePerRoom
                ? `
            <div class="detail-row">
              <span class="detail-label">🛏️ ${t('roomRate')}${booking.rooms > 1 ? ` (${booking.rooms} ${t('rooms')})` : ''}</span>
              <span class="detail-value">${booking.rooms > 1 ? `${booking.rooms} × ` : ''}${formatCurrency(booking.ratePerRoom, booking.currency)}</span>
            </div>
            `
                : ''
            }
            ${
              booking.extraCharges && booking.extraCharges.totalExtraCharge > 0
                ? `
            <div class="detail-row" style="color: #f59e0b;">
              <span class="detail-label">➕ ${t('extraCharges')}</span>
              <span class="detail-value">${formatCurrency(booking.extraCharges.totalExtraCharge, booking.currency)}</span>
            </div>
            <div style="font-size: 11px; color: #888; text-align: right; margin-top: -4px; margin-bottom: 8px;">
              ${booking.extraCharges.extraAdults > 0 ? `${booking.extraCharges.extraAdults} ${t('extraAdults')}` : ''}
              ${booking.extraCharges.extraAdults > 0 && booking.extraCharges.extraChildren > 0 ? ', ' : ''}
              ${booking.extraCharges.extraChildren > 0 ? `${booking.extraCharges.extraChildren} ${t('extraChildren')}` : ''}
            </div>
            `
                : ''
            }
            ${
              booking.totalBeforeTax
                ? (() => {
                    const svc = Math.ceil(booking.totalBeforeTax! * 0.10);
                    const vat = Math.ceil(booking.totalBeforeTax! * 0.07);
                    return `
            <div style="border-top: 1px dashed #e5e5e5; margin: 8px 0; padding-top: 8px;">
              <div class="detail-row" style="font-size: 13px; color: #888;">
                <span class="detail-label">💵 ${t('roomRateBeforeTax')}</span>
                <span class="detail-value">${formatCurrency(booking.totalBeforeTax!, booking.currency)}</span>
              </div>
              <div class="detail-row" style="font-size: 13px; color: #888;">
                <span class="detail-label">📊 ${t('serviceCharge10')}</span>
                <span class="detail-value">${formatCurrency(svc, booking.currency)}</span>
              </div>
              <div class="detail-row" style="font-size: 13px; color: #888;">
                <span class="detail-label">📊 ${t('vat7')}</span>
                <span class="detail-value">${formatCurrency(vat, booking.currency)}</span>
              </div>
            </div>`;
                  })()
                : ''
            }
          </div>
          
          ${(() => {
            let paymentDisplay: string | null = null;
            if (booking.paymentCardBrand && booking.paymentCardLast4) {
              const brand = booking.paymentCardBrand.charAt(0).toUpperCase() + booking.paymentCardBrand.slice(1);
              paymentDisplay = `${brand} ****${booking.paymentCardLast4}`;
            } else if (booking.paymentMethod === 'promptpay') {
              paymentDisplay = 'PromptPay';
            } else if (booking.paymentStatus === 'paid' && booking.paymentMethod) {
              paymentDisplay = t('statusPaid');
            } else if (!booking.paymentStatus || booking.paymentStatus === 'pay_at_hotel') {
              paymentDisplay = t('skipPayment');
            }
            return paymentDisplay ? `
          <div class="detail-row" style="margin-top: 4px;">
            <span class="detail-label">💳 ${t('paymentTitle')}</span>
            <span class="detail-value" style="color: ${booking.paymentStatus === 'paid' ? '#00B900' : '#666'}; font-weight: ${booking.paymentStatus === 'paid' ? 'bold' : 'normal'};">${paymentDisplay}</span>
          </div>` : '';
          })()}

          <div class="detail-row total-row" style="background: #f8f9fa; margin: 8px -16px -16px; padding: 12px 16px; border-radius: 0 0 8px 8px;">
            <span class="detail-label" style="font-size: 16px; font-weight: bold;">💰 ${t('totalAmount')}</span>
            <span class="detail-value" style="color: ${statusColor}; font-weight: bold; font-size: 18px;">
              ${formatCurrency(booking.totalAmount, booking.currency)}
            </span>
          </div>
        </div>
        
        ${
          booking.status === 'confirmed' && booking.paymentStatus === 'paid'
            ? `
          <div style="margin-top: 12px; padding: 10px; background: #e8f5e9; border-left: 3px solid #00B900; border-radius: 4px; font-size: 12px; color: #1b5e20;">
            💳 ${t('paidBookingCannotModify')}
          </div>
        `
            : booking.isRefundable === false
              ? `
          <div style="margin-top: 12px; padding: 10px; background: #fff3cd; border-left: 3px solid #ffc107; border-radius: 4px; font-size: 12px; color: #856404;">
            ⛔ ${t('nonRefundableCannotModify')}
          </div>
        `
            : booking.status === 'confirmed' && canModifyBooking(booking.checkIn, deadlineDays)
              ? `
          <div class="booking-actions" style="display: flex; gap: 8px; margin-top: 12px;">
            <button 
              type="button"
              class="btn btn-outline"
              onclick="window.editBooking('${booking.id}')"
              style="flex: 1; padding: 12px; font-size: 14px;"
            >
              ✏️ ${t('editBooking')}
            </button>
            <button 
              type="button"
              class="btn btn-outline-danger"
              onclick="window.cancelBooking('${booking.id}', '${booking.confirmationNumber}')"
              style="flex: 1; padding: 12px; font-size: 14px;"
            >
              ❌ ${t('cancelBooking')}
            </button>
          </div>
        `
              : booking.status === 'confirmed'
                ? `
          <div style="margin-top: 12px; padding: 10px; background: #fff3cd; border-left: 3px solid #ffc107; border-radius: 4px; font-size: 12px; color: #856404;">
            ℹ️ ${t('cannotModifyBooking')}
          </div>
        `
                : ''
        }
      </div>
    `,
        'booking-item-card'
      );
    })
    .join('');

  container.innerHTML = `
    <div class="bookings-header">
      <h2>${t('myBookings')}</h2>
      <p class="bookings-count">${bookings.length} ${t('bookings')}</p>
    </div>
    ${bookingsHtml}
    <div style="padding: 20px 0; text-align: center;">
      <button 
        type="button" 
        class="btn btn-orange btn-block" 
        onclick="window.closeMyBookings()"
        style="max-width: 400px; margin: 0 auto;"
      >
        ${t('close')}
      </button>
    </div>
  `;

  window.closeMyBookings = () => {
    closeWindow();
  };

  // Initialize edit/cancel handlers
  initBookingHandlers();
}

/**
 * Initialize booking edit/cancel handlers
 */
function initBookingHandlers(): void {
  window.editBooking = (bookingId: string) => {
    console.log('[MyBookings] Edit booking:', bookingId);
    // Redirect to search page with editBooking parameter
    window.location.href = `/search?editBooking=${bookingId}`;
  };

  window.cancelBooking = async (bookingId: string, confirmationNumber: string) => {
    console.log('[MyBookings] Cancel booking:', bookingId);
    showCancelConfirmationModal(bookingId, confirmationNumber);
  };
}

/**
 * Show cancel confirmation modal
 */
function showCancelConfirmationModal(bookingId: string, confirmationNumber: string): void {
  const modalHtml = `
    <div id="cancel-booking-modal" class="modal-overlay" style="display: flex;">
      <div class="modal-content" style="max-width: 450px;">
        <div class="modal-header" style="background: #fff3cd; border-bottom: 2px solid #ffc107;">
          <h3 style="color: #856404;">⚠️ ${t('cancelBooking')}</h3>
          <button type="button" class="modal-close" onclick="window.closeCancelModal()">&times;</button>
        </div>
        
        <div class="modal-body">
          <p style="font-size: 16px; margin-bottom: 16px;">
            ${t('confirmCancelBooking')}
          </p>
          
          <div style="background: #f8f9fa; padding: 12px; border-radius: 8px; margin-bottom: 16px;">
            <strong>${t('confirmationNumber')}:</strong><br/>
            <span style="font-size: 18px; color: #dc3545;">${confirmationNumber}</span>
          </div>
          
          <div style="background: #fff3cd; padding: 12px; border-left: 3px solid #ffc107; border-radius: 4px; margin-bottom: 16px;">
            <strong style="color: #856404;">📋 ${t('cancellationPolicy')}</strong>
            <ul style="margin: 8px 0 0 0; padding-left: 20px; color: #856404; font-size: 14px;">
              <li>${t('cancellationPolicy3Days')}</li>
              <li>${t('bookingWillBeCancelled')}</li>
            </ul>
          </div>
          
          <p style="color: #dc3545; font-weight: bold; text-align: center;">
            ${t('actionCannotBeUndone')}
          </p>
        </div>
        
        <div class="modal-footer">
          <button type="button" class="btn btn-outline" onclick="window.closeCancelModal()">
            ${t('keepBooking')}
          </button>
          <button type="button" class="btn btn-danger" onclick="window.confirmCancelBooking('${bookingId}')">
            ❌ ${t('confirmCancel')}
          </button>
        </div>
      </div>
    </div>
  `;

  document.body.insertAdjacentHTML('beforeend', modalHtml);

  // Close modal on overlay click
  const modal = document.getElementById('cancel-booking-modal');
  modal?.addEventListener('click', e => {
    if (e.target === modal) {
      window.closeCancelModal();
    }
  });

  // Close modal on ESC key
  const handleEsc = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      window.closeCancelModal();
      document.removeEventListener('keydown', handleEsc);
    }
  };
  document.addEventListener('keydown', handleEsc);

  window.closeCancelModal = () => {
    modal?.remove();
  };

  window.confirmCancelBooking = async (bookingId: string) => {
    try {
      const { getTenantId } = await import('../config');
      const { getCurrentLanguage } = await import('../i18n');
      const tenantId = getTenantId();
      const language = getCurrentLanguage();

      const result = await apiFetch<{
        success: boolean;
        data: Booking;
        notifyMessage?: import('../types').LineMessage;
      }>(
        `/api/${tenantId}/bookings/${bookingId}/cancel`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ language }),
        }
      );

      if (result.success) {
        // Send LINE message via LIFF if provided (no cost - user sends message)
        if (result.notifyMessage) {
          try {
            const { sendMessage } = await import('../liff');
            await sendMessage(result.notifyMessage);
            console.log('[MyBookings] Cancellation message sent via LIFF');
          } catch (msgError) {
            console.warn('[MyBookings] Failed to send cancellation message:', msgError);
            // Don't block UI if message send fails
          }
        }

        // Show custom toast instead of alert (to avoid domain name)
        showSuccessToast(t('bookingCancelledSuccess'));
        window.closeCancelModal();
        // Reload page
        initMyBookingsPage();
      } else {
        showErrorToast(t('errorCancellingBooking'));
      }
    } catch (error) {
      console.error('[MyBookings] Error cancelling booking:', error);
      showErrorToast(t('errorNetwork'));
    }
  };
}

// Extend window
declare global {
  interface Window {
    navigateToSearch: () => void;
    closeMyBookings: () => void;
    editBooking: (bookingId: string) => void;
    cancelBooking: (bookingId: string, confirmationNumber: string) => void;
    closeCancelModal: () => void;
    confirmCancelBooking: (bookingId: string) => void;
  }
}
